import React from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, HeartHandshake, ShieldCheck, Users2, Target, Compass,
  Stethoscope, Building2, Lock, Globe2, ArrowRight, Quote, CheckCircle2,
} from 'lucide-react';
import { BrandName } from '../components/BrandName';
import { BrandLogo } from '../components/BrandLogo';
import AppFooter from '../components/AppFooter';

/**
 * Iter 127 — About Us page. Reachable from the top-nav "About" dropdown.
 * Tells the Physiogenic story, mission, and goals with an editorial layout
 * that mirrors the landing page aesthetic (navy #003D80 + amber #FFB000 on
 * cream #FAF6EE). All copy is verbatim per the founder's brief; no auto-
 * generated marketing filler.
 */
export default function AboutUs() {
  return (
    <div className="min-h-screen bg-[#FAF6EE] text-[#1A2332]">
      <TopBar/>

      {/* ─── Hero ───────────────────────────────────────────────── */}
      {/* Iter 144 — pb-0 + items-end so the portrait sits flush on the next
          section's separator line and the CTA→picture gap is tighter. */}
      <section className="relative overflow-hidden pt-14 md:pt-24 pb-0 px-6 md:px-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 pb-10 md:pb-14">
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#FFB000] font-bold mb-4" data-testid="about-eyebrow">
              About Physiogenic
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight" data-testid="about-title">
              An entire <span className="text-[#003D80]">allied&nbsp;healthcare</span> management system,
              built with the <span className="italic">private clinic</span> in mind.
            </h1>
            <p className="mt-6 text-lg text-[#3A4A5E] max-w-xl leading-relaxed" data-testid="about-lede">
              <BrandName/> was founded by physiotherapists and clinic owners who spent
              too many evenings piecing together five different pieces of software just to
              run one clinic. We shipped Physiogenic to end that.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/start-clinic" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="about-cta-start">
                Start your clinic <ArrowRight className="w-4 h-4"/>
              </Link>
              <Link to="/platform" className="text-sm inline-flex items-center gap-2 px-5 py-2 rounded-lg border border-[#003D80]/25 hover:border-[#003D80] transition" data-testid="about-cta-platform">
                Explore the platform <ArrowRight className="w-4 h-4"/>
              </Link>
            </div>
          </div>

          {/* Portrait — branded male therapist (navy scrub + gold PHYSIOGENIC logo).
              Iter 129 — user asked to drop the female pair from the About hero and
              use the single branded male portrait so every visible clinician in the
              About + Platform pages wears the same clinic uniform + logo. */}
          <div className="lg:col-span-5 relative h-[360px] sm:h-[440px] lg:h-[560px]" data-testid="about-hero-portrait">
            <img src="/therapist-portrait-male.png" alt="Physiogenic-branded physiotherapist"
                 className="absolute bottom-0 left-1/2 -translate-x-1/2 h-full w-auto object-contain drop-shadow-[0_25px_45px_rgba(0,0,0,0.35)] z-20"/>
            {/* Amber halo */}
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-[70%] h-24 rounded-[50%] bg-[#FFB000]/25 blur-3xl z-0"/>
          </div>
        </div>
      </section>

      {/* ─── Who We Are + Why We Built This ────────────────────── */}
      <section className="px-6 md:px-10 py-14 md:py-20 bg-white/70 border-y border-[#E8DFC8]">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 md:gap-16">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#003D80] font-bold mb-3">Who we are</div>
            <h2 className="font-heading font-bold text-3xl md:text-4xl leading-tight mb-4">
              A team of clinicians, engineers and privacy specialists.
            </h2>
            <p className="text-[#3A4A5E] leading-relaxed">
              We're a Warsaw-based team split evenly between practising allied health
              professionals — physiotherapists, osteopaths, chiropractors and massage
              therapists — and the engineers who build the tools they wish they had.
              Every workflow you'll see in Physiogenic came from a real clinic day
              that broke on paper, on WhatsApp, or on a spreadsheet.
            </p>
            <p className="text-[#3A4A5E] leading-relaxed mt-4">
              We're independent, self-funded, and headquartered under EU jurisdiction.
              We do not sell your data. We do not train foundation models on your
              patient charts. And we ship every feature under the same GDPR + HIPAA
              guardrails our own clinics have to answer to.
            </p>
          </div>
          <div className="relative">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#003D80] font-bold mb-3">Why we built this</div>
            <blockquote className="border-l-4 border-[#FFB000] pl-6 py-2 italic text-lg text-[#1A2332] leading-relaxed">
              <Quote className="w-6 h-6 text-[#FFB000] mb-3"/>
              Allied healthcare is a $400 billion global category held together by
              consumer calendar apps, Word templates and hand-written invoices. Every
              clinician we know spends 40&nbsp;% of their week doing admin instead of
              treating patients. That is the problem we get up to solve.
            </blockquote>
            <div className="mt-5 flex items-center gap-3 text-sm text-[#3A4A5E]">
              <div className="w-10 h-10 rounded-full bg-[#003D80] text-white grid place-items-center font-bold">P</div>
              <div>
                <div className="font-semibold text-[#1A2332]">The Physiogenic team</div>
                <div className="text-xs">Founders & clinicians · Warsaw</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Mission ──────────────────────────────────────────── */}
      <section className="px-6 md:px-10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto">
          <SectionEyebrow icon={Compass}>Our mission</SectionEyebrow>
          <h2 className="font-heading font-bold text-3xl md:text-4xl max-w-3xl leading-tight">
            Three commitments that guide every product decision we make.
          </h2>
          <div className="grid md:grid-cols-3 gap-5 mt-10" data-testid="about-mission-grid">
            <MissionCard
              index="01"
              icon={HeartHandshake}
              title="Give clinicians their evenings back."
              body="Automate the admin work — SOAP note dictation, invoicing, insurance codes, reminders — so a physiotherapist can finish their day at the clinic, not at their kitchen table."
              testid="mission-1"/>
            <MissionCard
              index="02"
              icon={ShieldCheck}
              title="Treat patient data like it belongs to the patient."
              body="Deliver HIPAA + GDPR + EHDS-grade privacy as a default, not a paid add-on. Encrypted at rest, encrypted in transit, region-locked, and never sold, aggregated, or fed to third-party AI training pipelines."
              testid="mission-2"/>
            <MissionCard
              index="03"
              icon={Sparkles}
              title="Put every discipline on equal footing."
              body="Physiotherapy, osteopathy, chiropractic, massage, fitness and wellness — one chart, one billing engine, one patient identity, so multi-modal clinics stop paying for four platforms."
              testid="mission-3"/>
          </div>
        </div>
      </section>

      {/* ─── Goals ────────────────────────────────────────────── */}
      <section className="px-6 md:px-10 py-16 md:py-24 bg-[#003D80] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#FFB000] font-bold mb-3 inline-flex items-center gap-2">
            <Target className="w-3.5 h-3.5"/> Our goals
          </div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white max-w-3xl leading-tight">
            What we're aiming for, in numbers and outcomes.
          </h2>
          <div className="grid md:grid-cols-3 gap-5 mt-10" data-testid="about-goals-grid">
            <GoalCard
              index="Goal 01"
              stat="10,000"
              statSuffix="private clinics on one platform"
              body="Reach ten thousand independent allied-health clinics across Europe, North America and APAC — proof that vertical-specific SaaS beats horizontal EHRs for this market."
              testid="goal-1"/>
            <GoalCard
              index="Goal 02"
              stat="< 3 min"
              statSuffix="from patient in the room to SOAP note filed"
              body="Compress the clinical documentation loop with AI Scribe + AI Dx from 15+ minutes to under three, without sacrificing red-flag screening rigor or HIPAA compliance."
              testid="goal-2"/>
            <GoalCard
              index="Goal 03"
              stat="100 %"
              statSuffix="patient-owned data, region-locked"
              body="Zero cross-border patient data flows without explicit opt-in. Twelve residency regions and counting so every clinic can meet its regulator on their terms."
              testid="goal-3"/>
          </div>
        </div>
      </section>

      {/* ─── What we built — Platform overview ───────────────── */}
      <section className="px-6 md:px-10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <SectionEyebrow icon={Stethoscope}>What we built</SectionEyebrow>
            <h2 className="font-heading font-bold text-3xl md:text-4xl leading-tight mb-5">
              An entire allied healthcare management system.
            </h2>
            <p className="text-[#3A4A5E] leading-relaxed">
              Not an EHR bolted onto a booking widget. Not a booking widget bolted onto
              an invoice tool. Physiogenic covers the full life-cycle of a private
              clinic: <strong>online booking · digital intake · AI-assisted charting ·
              treatment plans · exercise HEP · outcomes tracking · financials · tax
              exports · inventory · marketing · student supervision</strong>. One login,
              one patient chart, one bill.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-[#1A2332]">
              {[
                'Multi-discipline chart — physio, osteo, chiro, massage, fitness in one record.',
                'Multi-clinic aware — franchises + networks share patients, split reporting.',
                'Multi-language — EN · PL · ES out of the box, more on request.',
                'Multi-currency invoicing with VAT-exempt handling for healthcare.',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#1B5E20] shrink-0 mt-0.5"/>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/platform" className="mt-8 inline-flex items-center gap-2 text-[#003D80] font-semibold hover:underline" data-testid="about-explore-platform">
              Explore every feature <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            {/* Iter 129 — replaced the unbranded stock photo with a branded
                visual: the Physiogenic-uniformed therapist against a navy
                gradient tile with feature accents. Keeps every visible
                clinician on-brand. */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3]"
                 style={{ background: 'linear-gradient(135deg, #003D80 0%, #001E40 100%)' }}
                 data-testid="about-platform-photo">
              <img src="/therapist-portrait-male.png"
                   alt="Physiogenic-branded physiotherapist beside the platform overview"
                   className="absolute bottom-0 right-2 h-[105%] w-auto object-contain drop-shadow-[0_25px_45px_rgba(0,0,0,0.55)]"
                   loading="lazy"/>
              {/* Floating "module chips" over the gradient to visually communicate
                  the "one platform, many modules" story. */}
              <div className="absolute top-6 left-6 space-y-2 max-w-[55%]">
                {['Online booking', 'AI Scribe', 'Treatment plans', 'Financials', 'Marketing'].map((label, i) => (
                  <div key={label} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 mr-2 text-[11px] font-semibold text-white"
                       style={{ opacity: 1 - (i * 0.08) }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#FFB000]"/>
                    {label}
                  </div>
                ))}
              </div>
              <div className="absolute bottom-4 left-6 text-[10px] tracking-[0.2em] uppercase font-bold text-[#FFB000]">
                One Platform · Every Discipline
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Built for private clinics ──────────────────────── */}
      <section className="px-6 md:px-10 py-16 md:py-20 bg-white/70 border-y border-[#E8DFC8]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            {/* Iter 144 — replaced the male-portrait editorial tile with a real
                clinic visual: front desk + computer running Physiogenic. */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/3] bg-[#FAF6EE]"
                 data-testid="about-clinic-photo">
              <img src="/clinic-computer.jpg"
                   alt="Private clinic front desk with Physiogenic running on the computer"
                   className="absolute inset-0 w-full h-full object-cover"
                   loading="lazy"/>
              <div className="absolute inset-0 bg-gradient-to-t from-[#001E40]/55 via-transparent to-transparent"/>
              <div className="absolute bottom-4 left-6 z-10">
                <div className="text-[9px] tracking-[0.28em] uppercase font-bold text-[#FFB000] mb-1">Your clinic · your platform</div>
                <div className="font-heading font-black text-2xl leading-none text-white">Independent</div>
                <div className="font-heading font-black text-2xl leading-none text-white italic">Clinic-first</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <SectionEyebrow icon={Building2}>Built with the private clinic in mind</SectionEyebrow>
            <h2 className="font-heading font-bold text-3xl md:text-4xl leading-tight mb-5">
              The independent clinic is the hero, not an afterthought.
            </h2>
            <p className="text-[#3A4A5E] leading-relaxed">
              Enterprise EHRs are designed for hospitals with an IT department. Physiogenic is
              designed for the clinic owner who is also the head therapist, the marketing
              lead and the payroll manager on any given Tuesday. Every workflow is one
              tap away, every setting has a sane default, and every AI recommendation is
              transparent about its confidence.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <FeatureBullet label="Zero-onboarding friction" hint="Sign up → book → chart → invoice in 10 minutes"/>
              <FeatureBullet label="Co-created with clinics" hint="New features are built from real clinic requests — yours included"/>
              <FeatureBullet label="Own your brand" hint="Custom logo, colours, tagline, PDF templates"/>
              <FeatureBullet label="Own your data" hint="Full export + right-to-erasure buttons built in"/>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Security & compliance ──────────────────────────── */}
      <section className="px-6 md:px-10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto">
          <SectionEyebrow icon={Lock}>All patient data secured</SectionEyebrow>
          <h2 className="font-heading font-bold text-3xl md:text-4xl leading-tight max-w-3xl mb-5">
            Privacy is not a feature. It's the substrate everything else runs on.
          </h2>
          <p className="text-[#3A4A5E] leading-relaxed max-w-3xl">
            Physiogenic is HIPAA-safeguard aligned, GDPR-compliant, EHDS-ready and
            certified for twelve data-residency regions. Every patient record is
            encrypted at rest with per-clinic keys, mirrored across regulator-approved
            regions, and auditable via HMAC-signed compliance PDFs your regulator can
            independently verify.
          </p>
          <div className="grid md:grid-cols-4 gap-4 mt-10" data-testid="about-security-grid">
            <SecurityStat icon={ShieldCheck} label="HIPAA safeguards" value="Aligned"/>
            <SecurityStat icon={Globe2}      label="GDPR + EHDS"       value="Compliant"/>
            <SecurityStat icon={Lock}        label="Encryption"        value="AES-256"/>
            <SecurityStat icon={Users2}      label="Data residency"    value="12 regions"/>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/legal/hipaa" className="text-sm inline-flex items-center gap-2 px-4 py-2 border border-[#003D80]/25 rounded-lg hover:border-[#003D80] transition" data-testid="about-hipaa-link">
              HIPAA safeguards <ArrowRight className="w-3.5 h-3.5"/>
            </Link>
            <Link to="/legal/gdpr" className="text-sm inline-flex items-center gap-2 px-4 py-2 border border-[#003D80]/25 rounded-lg hover:border-[#003D80] transition" data-testid="about-gdpr-link">
              GDPR rights <ArrowRight className="w-3.5 h-3.5"/>
            </Link>
            <Link to="/legal/global" className="text-sm inline-flex items-center gap-2 px-4 py-2 border border-[#003D80]/25 rounded-lg hover:border-[#003D80] transition" data-testid="about-global-link">
              Global compliance <ArrowRight className="w-3.5 h-3.5"/>
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Bottom CTA ─────────────────────────────────────── */}
      <section className="px-6 md:px-10 py-16 md:py-20 bg-[#003D80]">
        <div className="max-w-4xl mx-auto text-center">
          <BrandLogo size={64} src="/logo.jpg" className="mx-auto mb-4"/>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white leading-tight">
            Ready to give your clinicians their evenings back?
          </h2>
          <p className="text-white/80 mt-4 max-w-xl mx-auto">
            30-day free trial. Cancel anytime. No card required to explore.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/start-clinic" className="btn-primary text-sm inline-flex items-center gap-2" data-testid="about-footer-start">
              Start your clinic free <ArrowRight className="w-4 h-4"/>
            </Link>
            <Link to="/clinics" className="text-sm px-5 py-2 rounded-lg border border-white/25 text-white hover:border-white transition inline-flex items-center gap-2" data-testid="about-footer-directory">
              Browse the clinic network <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
        </div>
      </section>

      <AppFooter/>
    </div>
  );
}

/* ─── Presentational sub-components ────────────────────────────── */
function TopBar() {
  return (
    <div className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-[#E8DFC8]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="about-brand-back">
          <BrandLogo size={48} src="/logo.jpg"/>
          <span className="font-heading font-bold tracking-tight text-lg"><BrandName/></span>
        </Link>
        <Link to="/" className="text-sm text-[#3A4A5E] hover:text-[#003D80] inline-flex items-center gap-1.5" data-testid="about-home-link">
          <ArrowRight className="w-3.5 h-3.5 rotate-180"/> Back to homepage
        </Link>
      </div>
    </div>
  );
}

function SectionEyebrow({ icon: Icon, children }) {
  return (
    <div className="text-[10px] tracking-[0.28em] uppercase text-[#003D80] font-bold mb-3 inline-flex items-center gap-2">
      <Icon className="w-3.5 h-3.5"/> {children}
    </div>
  );
}

function MissionCard({ index, icon: Icon, title, body, testid }) {
  return (
    <div className="surface bg-white rounded-2xl border border-[#E8DFC8] p-6 hover:border-[#FFB000] transition group" data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <div className="w-11 h-11 rounded-xl bg-[#FFB000]/15 text-[#8A5A00] grid place-items-center group-hover:bg-[#FFB000]/25 transition">
          <Icon className="w-5 h-5" strokeWidth={1.7}/>
        </div>
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#003D80]/50">{index}</div>
      </div>
      <h3 className="font-heading font-bold text-lg leading-snug mb-2">{title}</h3>
      <p className="text-sm text-[#3A4A5E] leading-relaxed">{body}</p>
    </div>
  );
}

function GoalCard({ index, stat, statSuffix, body, testid }) {
  return (
    <div className="rounded-2xl border border-white/15 bg-white/5 p-6 hover:bg-white/10 transition backdrop-blur-sm" data-testid={testid}>
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#FFB000] mb-3">{index}</div>
      <div className="font-heading font-extrabold text-4xl leading-none text-white">{stat}</div>
      <div className="text-xs text-white/70 mt-1 mb-4">{statSuffix}</div>
      <p className="text-sm text-white/85 leading-relaxed">{body}</p>
    </div>
  );
}

function FeatureBullet({ label, hint }) {
  return (
    <div className="rounded-xl border border-[#E8DFC8] bg-white p-3">
      <div className="text-sm font-semibold text-[#1A2332]">{label}</div>
      <div className="text-xs text-[#3A4A5E] mt-0.5">{hint}</div>
    </div>
  );
}

function SecurityStat({ icon: Icon, label, value }) {
  return (
    <div className="surface bg-white rounded-2xl border border-[#E8DFC8] p-5 hover:border-[#003D80] transition">
      <Icon className="w-5 h-5 text-[#003D80] mb-3" strokeWidth={1.7}/>
      <div className="font-heading font-bold text-xl leading-none">{value}</div>
      <div className="text-xs text-[#3A4A5E] mt-1">{label}</div>
    </div>
  );
}
